/**
 * Menu Availability — determines if a menu is currently available based on:
 * 1. Date range (validFromDate → validToDate)
 * 2. Time range (validFromTime → validToTime), which MAY cross midnight
 *
 * Uses the venue's timezone for all calculations.
 */

import { minutesOfDay, venueNow, type VenueNow } from './promotionSchedule'

export interface MenuAvailabilityInput {
  validFromDate: string | null  // 'YYYY-MM-DD'
  validToDate: string | null    // 'YYYY-MM-DD'
  validFromTime: string | null  // 'HH:MM' or 'HH:MM:SS' (24h)
  validToTime: string | null    // 'HH:MM' or 'HH:MM:SS' (24h)
}

export interface MenuAvailabilityResult {
  /** Whether the menu is currently available for ordering */
  isAvailable: boolean
  /** Human-readable reason when not available */
  reason: string | null
  /** When the menu becomes available next (if not currently available) */
  availableFrom: string | null
  /** When the menu stops being available (if currently available) */
  availableUntil: string | null
}

/** Where the clock sits relative to a daily service window. */
interface ServiceWindow {
  /** Is the venue inside the window right now? */
  live: boolean
  /**
   * The calendar day the CURRENT (or most recent) service began on, 'YYYY-MM-DD'.
   *
   * For a window crossing midnight this is yesterday whenever the clock is past
   * midnight but still before the closing time — 01:00 on Saturday belongs to
   * Friday's 12:00–02:00 service, and the date range must be judged against Friday.
   */
  startedOnDate: string
  /** When not live: is the window still to come today, or already finished? */
  phase: 'before' | 'after' | null
}

/** The day `days` after `date` ('YYYY-MM-DD'), as 'YYYY-MM-DD'. */
function shiftDate(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

/** Minutes since midnight back to 'HH:MM', so callers get one clock format whatever the DB sent. */
function toClock(mins: number): string {
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`
}

/**
 * Is `now` inside the menu's daily serving window?
 *
 * **Handles a window that CROSSES MIDNIGHT (12:00–02:00), which is the normal shape of a
 * restaurant's evening service, not an edge case.** This used to be a plain string
 * comparison — `now > validToTime` → closed — and for an overnight window that is true at
 * every hour of the day: at 16:00, `"16:00" > "02:00"`; at 23:00, `"23:00" > "02:00"`; and
 * at 01:00 the other branch fires because `"01:00" < "12:00"`. A menu set 12:00–02:00 was
 * therefore unavailable 24 hours a day, with its items still listed and priced and every
 * add button dead. Found in production on a 237-item hotel menu.
 *
 * The end of the window is INCLUSIVE, matching the long-standing behaviour a guest sees at
 * exactly the closing minute (and the test that pins it).
 */
function serviceWindow(now: VenueNow, from: number | null, to: number | null): ServiceWindow {
  const today = { startedOnDate: now.date, phase: null } as const

  // No window, or a half-open one. An owner who filled in only a start time meant
  // "from then on", not "for zero minutes".
  if (from == null && to == null) return { live: true, ...today }
  if (from != null && to == null) {
    return now.minutes >= from
      ? { live: true, ...today }
      : { live: false, startedOnDate: now.date, phase: 'before' }
  }
  if (from == null && to != null) {
    return now.minutes <= to
      ? { live: true, ...today }
      : { live: false, startedOnDate: now.date, phase: 'after' }
  }

  const start = from as number
  const end = to as number

  // Equal start and end reads as all day, the same way `isVenueCurrentlyOpen` treats
  // open_time === close_time. An owner saving 00:00–00:00 means "always", never "never".
  if (start === end) return { live: true, ...today }

  if (start < end) {
    if (now.minutes < start) return { live: false, startedOnDate: now.date, phase: 'before' }
    if (now.minutes > end) return { live: false, startedOnDate: now.date, phase: 'after' }
    return { live: true, ...today }
  }

  // Crossing midnight. Before the closing time we are still inside YESTERDAY's service.
  if (now.minutes <= end) {
    return { live: true, startedOnDate: shiftDate(now.date, -1), phase: null }
  }
  if (now.minutes >= start) return { live: true, ...today }

  // The gap between last night's close and tonight's open — it reopens later today.
  return { live: false, startedOnDate: now.date, phase: 'before' }
}

/**
 * Check if a menu is currently available based on its date/time settings.
 *
 * @param menu — the menu's validity fields
 * @param timezone — venue timezone (IANA, e.g. 'Asia/Dubai')
 * @returns availability result with reason and timing info
 */
export function isMenuCurrentlyAvailable(
  menu: MenuAvailabilityInput,
  timezone: string = 'Asia/Dubai',
): MenuAvailabilityResult {
  const { validFromDate, validToDate, validFromTime, validToTime } = menu

  // No restrictions → always available
  if (!validFromDate && !validToDate && !validFromTime && !validToTime) {
    return { isAvailable: true, reason: null, availableFrom: null, availableUntil: null }
  }

  const now = venueNow(new Date(), timezone)

  // Parsed, not compared as strings: `valid_from_time` / `valid_to_time` are Postgres
  // TIME columns and arrive as 'HH:MM:SS', while the clock is 'HH:MM'. Comparing those
  // directly made a menu read as closed for the first minute after it opened, because
  // '12:00' sorts before its own '12:00:00'.
  const from = minutesOfDay(validFromTime)
  const to = minutesOfDay(validToTime)
  const window = serviceWindow(now, from, to)

  // ── Date check ────────────────────────────────────────────────────────────
  // Judged against the day the SERVICE started, not the wall-clock date, so a Friday
  // 12:00–02:00 menu is still Friday's menu at 01:00 on Saturday.
  if (validFromDate && window.startedOnDate < validFromDate) {
    return {
      isAvailable: false,
      reason: `Available from ${formatDisplayDate(validFromDate)}`,
      availableFrom: validFromDate,
      availableUntil: validToDate,
    }
  }

  if (validToDate && window.startedOnDate > validToDate) {
    return {
      isAvailable: false,
      reason: 'This menu is no longer available',
      availableFrom: null,
      availableUntil: null,
    }
  }

  // ── Time check ────────────────────────────────────────────────────────────
  if (!window.live) {
    if (window.phase === 'before' && from != null) {
      return {
        isAvailable: false,
        reason: `Available from ${formatDisplayTime(toClock(from))}`,
        availableFrom: toClock(from),
        availableUntil: to != null ? toClock(to) : null,
      }
    }
    if (window.phase === 'after' && to != null) {
      return {
        isAvailable: false,
        reason: `Available until ${formatDisplayTime(toClock(to))}`,
        availableFrom: from != null ? toClock(from) : null,
        availableUntil: null,
      }
    }
  }

  return {
    isAvailable: true,
    reason: null,
    availableFrom: from != null ? toClock(from) : null,
    availableUntil: to != null ? toClock(to) : null,
  }
}

/** Format 'YYYY-MM-DD' to readable date */
function formatDisplayDate(dateStr: string): string {
  try {
    const d = new Date(dateStr + 'T00:00:00')
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  } catch {
    return dateStr
  }
}

/** Format 'HH:MM' to 12h time */
function formatDisplayTime(timeStr: string): string {
  const [h, m] = timeStr.split(':').map(Number)
  if (h === undefined || m === undefined) return timeStr
  const ampm = h >= 12 ? 'PM' : 'AM'
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`
}
